import { get } from 'lodash-es';
import { OperatingSystemService } from 'ui-components/services/operating-system-service';
import { emptyArray } from 'ui-components/default-props/empty';
import { ShippingServiceService } from '../services/shipping-service-service';
import { InsuranceProviders } from '../enums/insurance-providers';
import { SellerProviderStatusEnum } from '../enums/seller-provider-status';

export const presetService = {
  convertIdToLetter(value) {
    if (!value) return;
    return String.fromCharCode(value);
  },

  convertLetterToId(letter) {
    if (!letter) return undefined;
    const charCode = letter.charCodeAt(0);
    return charCode;
  },

  mapToPackageKeyValue(item) {
    if (item) {
      return {
        key: item.name,
        value: `${item.packageTypeId}-${item.isCustom}-${item.carrierId}`,
        packageTypeId: item.packageTypeId,
      };
    }
  },

  getHotkeyPrefix() {
    return OperatingSystemService.isMac() ? 'Cmd' : 'Ctrl';
  },

  generatePresetDropdownEntry(letter) {
    if (!letter) return { value: '' };
    const ctrlOrCmd = OperatingSystemService.isMac() ? 'Cmd' : 'Ctrl';
    const letterUppercase = letter.toUpperCase();
    return {
      key: letter,
      value: letter,
      display: `${ctrlOrCmd} + ${letterUppercase}`,
    };
  },

  getDropdownPresetHotkeys() {
    const ids = [];
    for (let i = 0; i < 10; i++) ids.push(this.generatePresetDropdownEntry(`${i}`));
    for (let i = 0; i < 26; i++) {
      const letterId = this.convertLetterToId('a') + i;
      const letter = this.convertIdToLetter(letterId);
      ids.push(this.generatePresetDropdownEntry(letter));
    }
    return ids;
  },

  removePresetById(presetsInState, actionId) {
    const delIndex = presetsInState.findIndex((preset) => preset.presetId === actionId);
    if (delIndex > -1) {
      const presets = [...presetsInState.slice(0, delIndex), ...presetsInState.slice(delIndex + 1)];
      return presets;
    }
    return presetsInState;
  },

  // TODO: The below 3 methods are hard to follow. Could move logic to ApplyPresetDropdownContainer except hotkeys would need this too somehow
  generateHotkeyConfigPayload(hotkeyPreset, packages, services, sellerLabelProviders, sellerShipFroms) {
    return this.generateShipmentConfigPayload(
      this.mapPresetToKeyValue(hotkeyPreset, packages, services, sellerLabelProviders),
      sellerShipFroms,
    );
  },

  generateShipmentConfigPayload(preset, sellerShipFroms) {
    const payload = {};
    const presetOptions = {};
    Object.keys(preset.presetOptions).forEach((key) => {
      if (preset.presetOptions[key]) {
        presetOptions[key] = preset.presetOptions[key];
      }
    });
    payload.presetOptions = presetOptions;
    payload.packages = get(preset, 'packages', emptyArray);
    payload.options = get(preset, 'options');
    if (payload.presetOptions.shipFromId) {
      const sellerShipFrom = sellerShipFroms.find((shipFrom) => shipFrom.shipFromId === payload.presetOptions.shipFromId);
      if (!sellerShipFrom) {
        delete payload.presetOptions.shipFromId;
      }
    }
    if (payload.presetOptions.serviceId) {
      payload.presetOptions.serviceCollectionGuid = null;
    }
    if (payload.presetOptions.serviceCollectionGuid) {
      payload.presetOptions.sellerLabelProviderId = null;
      payload.presetOptions.serviceId = null;
    }
    return payload;
  },

  mapPresetToKeyValue(item, allPackages, services, sellerLabelProviders) {
    const insurances = Object.values(InsuranceProviders);
    const serviceId =
      typeof item?.presetOptions?.shippingService === 'object'
        ? item?.presetOptions?.shippingService?.serviceId || item?.presetOptions?.fulfillmentService
        : item?.presetOptions?.shippingService || item?.presetOptions?.fulfillmentService;
    const backendOptions = {
      shipFromId: get(item, 'presetOptions.warehouseId'),
      serviceId,
      serviceCollectionGuid: item?.presetOptions?.shippingService?.serviceCollectionGuid,
      packageTypeId: get(item, 'presetOptions.packageTypeId'),
      weight: get(item, 'presetOptions.weight', 0),
      dimensions: get(item, 'presetOptions.dimensions'),
      confirmation: get(item, 'presetOptions.confirmationId'),
      insuranceProvider: get(item, 'presetOptions.insuranceProviderId'),
      insurance: get(item, 'presetOptions.insurance'),
    };
    const presetOptions = {};
    const packages = [];
    const options = {};
    Object.keys(backendOptions).forEach((key) => {
      if (key === 'confirmation' && backendOptions[key]) {
        options.confirmation = { confirmationId: backendOptions[key] };
        options.confirmationEnumId = backendOptions[key]; // deals with discrepancy in hotkey vs. dropdown behavior
        return;
      }
      if (key === 'insuranceProvider' && backendOptions[key]) {
        presetOptions[key] = insurances.find((insurance) => insurance.insuranceProviderId === backendOptions[key])?.name;
        return;
      }
      if (allPackages && key === 'packageTypeId') {
        const targetPackage = allPackages.find((p) => `${p.packageTypeId}` === `${backendOptions[key]}`);
        const insuredValue = get(backendOptions, 'insurance.value');
        const weight = get(backendOptions, 'weight');
        const dimensions = get(item, 'presetOptions.dimensions');
        const newPackage = {
          insuredValue,
          weight,
          dimensions,
        };
        if (targetPackage) {
          newPackage.packageTypeId = `${backendOptions[key]}`;
          newPackage.packageId = get(targetPackage, 'value') || get(this.mapToPackageKeyValue(targetPackage), 'value');
          if (targetPackage.isCustom && targetPackage.dimensions && !backendOptions.dimensions) {
            newPackage.dimensions = targetPackage.dimensions;
          }
        }
        if (dimensions || insuredValue || weight || newPackage.packageTypeId) {
          packages.push(newPackage);
        }
        return;
      }
      if (key === 'serviceId' && backendOptions[key]) {
        presetOptions[key] = `${backendOptions[key]}`;
        presetOptions.sellerLabelProviderId = this.getSellerLabelProviderId(item, backendOptions[key], services, sellerLabelProviders);
        return;
      }
      if (backendOptions[key]) {
        presetOptions[key] = typeof backendOptions[key] === 'object' ? backendOptions[key] : `${backendOptions[key]}`;
      }
    });
    return {
      ...item,
      presetOptions,
      packages,
      options,
    };
  },

  getConfirmationTypes(selectedServiceInfo, carriers) {
    const { carrierId } = selectedServiceInfo || {};
    const carrierInfo = carriers.find((carrier) => carrier.carrierId === carrierId);
    return carrierInfo?.confirmations?.map((cT) => ({
      value: cT.confirmationId,
      key: cT.name,
    }));
  },

  getSellerLabelProviderId(sellerPreset, serviceId, services, sellerLabelProviders) {
    const includedSellerLabelProviderId = sellerPreset?.presetOptions?.shippingService?.sellerLabelProviderId;
    const matchingProviderIsActive =
      sellerLabelProviders.find((slp) => slp.sellerLabelProviderId === includedSellerLabelProviderId)?.status ===
      SellerProviderStatusEnum.active.value;
    if (matchingProviderIsActive) {
      return includedSellerLabelProviderId;
    }
    const primaryId = ShippingServiceService.getPrimarySellerLabelProviderId(serviceId, services, sellerLabelProviders);
    if (primaryId) {
      return primaryId;
    }
    // sometimes sellers get into states where they have no primary account set for a provider so just find the first
    const shippingService = services.find((service) => service.serviceId === serviceId);
    return sellerLabelProviders.find((slp) => slp.labelProviderId === shippingService?.labelProviderId)?.sellerLabelProviderId;
  },
};
